<template>
  <div class="planview">
    <transition name="bubble-fade">
    <div v-if="bubbleVisible" class="bubble-notification" >
      
      Loaded stored plan.
      <a @click="resetPreferences()">Create a new plan instead.</a>
      <button class="close-btn" @click="closeBubble()">×</button>
    </div>
    </transition>

    <div v-if="IsSaveModalOpen"></div>
    <div v-else-if="isLoadingSchedule || isLoadingProgram" class="noplan">
      <div class="noplan-content">
        <img :src="TumIcon" alt="Loading" class="loading-icon"/>
        <div>
          <div class="noplan-text-headline">Loading.</div>
          <div class="noplan-text">{{
              isLoadingSchedule ?
                  saveStart ? "Verifying that you stored plan is still valid." :
                    'Computing a possible plan according to your preferences.' :
                    'All module data for your study program is being retrieved.'
            }}
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="errorOccurred" class="noplan">
      <div class="noplan-content">
        <span class="material-symbols-outlined noplan-icon">error</span>
        <div>
          <div class="noplan-text-headline">Internal Error</div>
          <div class="noplan-text">An unexpected error has occurred on our server.
          </div>
        </div>
        <span class="material-symbols-outlined noplan-icon">rule_settings</span>
        <div>
          <div class="noplan-text-headline">What you can do</div>
          <ul class="noplan-list">
            <li>
              To help us improve our service, please
              <a class="clickable-text" @click="openModal('ReportModal')">file a report</a>.
            </li>
            <li>
              Adjust your preferences in the sidebar to generate a new plan.
            </li>
            <li>
              If the issue persists, please reload the page. Note that any unsaved inputs will be lost.
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-else-if="!reqPrefsSet" class="noplan">
      <div class="noplan-content">
        <span class="material-symbols-outlined noplan-icon">not_started</span>
        <div>
          <div class="noplan-text-headline">Welcome!</div>
          <div class="noplan-text">Set the basic choices in the sidebar to compute a possible study plan for you.
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!addedPassedModules" class="noplan"></div>
    <div v-else-if="!hasSolution " class="noplan">
     <div class="noplan-content">
      <span class="material-symbols-outlined noplan-icon">search_off</span>
      <div>
        <div class="noplan-text-headline">Sorry, no plan found!</div>
        <div class="noplan-text">
          <div v-if="violatedConstraints.length">
            <div>There is no study plan that fulfills your preferences and also all regulations of your degree program.</div>
            <div>Your preferences violate the following conditions in the study regulations:</div>
            <ul class="noplan-list">
              <li v-for="(constraint, index) in uniqueViolatedConstraints" :key="index" style="white-space: pre-line;">
                {{ constraint }}
              </li>
            </ul>
          </div>
          <div v-else>
            There is no study plan that fulfills your preferences and also all regulations of your degree program.
          </div>
        </div>
        </div>
        <span class="material-symbols-outlined noplan-icon">rule_settings</span>
        <div>
          <div>
            This is how you may fix the problem:
          </div>
          <li>
                <b>Enter all your <a class="clickable-text" @click="openModal('PassedModulesModal')">passed modules</a></b>. Note that all TUM degrees require that you pass
                some (specified) modules until the end of your second semester. Thus, there is no valid study plan if you are beyond your second semester and you do not enter that those modules as passed.
                </li>
                <li>
                  <b>More credits or exams per semester.</b> You can do this under "ADVANCED" in the sidebar. In particular, all TUM degrees have a limit for how many semester you can study them. If you select too few credits per semester, you cannot meet this limit anymore.
                  <br>
                </li>
                <li v-if="this.preferences.desiredModules.length !== 0">
                  <b>Fewer <a class="clickable-text" @click="openModal('DesiredModulesModal')">desired modules</a></b>. We show you only study plans that contain all of them, and you see this message if there is no such plan.
                </li>
                <li v-if="this.preferences.undesiredModules.length !== 0">
                  <b>Fewer <a class="clickable-text" @click="openModal('UndesiredModulesModal')">undesired modules</a></b>. If you exclude too many modules, the remaining modules may not be enough for a valid study plan.
                </li>
                <li v-if="this.preferences.degree === 'msc' && !this.preferences.de">
                  <b>More possible languages</b> for your modules. You can do this under "ADVANCED" in the sidebar.
                </li>
            </div>
            <span class="material-symbols-outlined noplan-icon">summarize</span>
            <div>
              <div class="noplan-text-headline">Incorrect? Give feedback.</div>
              <div class="noplan-text">If you think there should be a valid plan for your preferences, please <a
                  class="clickable-text" @click="openModal('ReportModal')">file a
                report</a>. We will then check how we can fix this problem in the StudyPlanner.
              </div>
        </div>
      </div>
      
        <div class="undo-container">
          <button class="undo-button" @click="undoLastChange" :disabled="!canUndo">
            Undo last change
          </button>

          <button v-if="!canUndo" class="undo-button" @click="resetPreferences">
            Reset preferences
          </button>
        </div>

    </div>
    <div v-else class="plan">
      <div v-if="ectsWarning" class="warning-container">
        <span class="material-symbols-outlined icon-warning">warning</span>
          <div>
            <div class="warning-message" v-html="ectsWarning?.message"></div>
          </div>
        </div>
      <div class="overview">
        <div class="overview-item">Summary</div>
        <div class="overview-item">ECTS: {{ totalECTS }}</div>
        <div class="overview-item">One possible plan according to your preferences. It
          is planned such that you complete your degree as soon as possible while trying to avoid overlapping lectures.
        </div>
      </div>

      <div v-for="item in plan" :key="item.semester"
           :class="{ 'semester--0': item.semester === 0, unfolded: unfoldStates[item.semester] }"
           class="semester">

        <div class="semester-details">
          <div v-if="item.semester !== 0" class="semester-overview"
               @click="toggleUnfolded(item.semester)">
            <div class="semester-info">
              <div class="semester-info-item">{{ item.semester }}</div>
              <div class="semester-info-item">{{ item.schedule.semesterDescription }}</div>
              <div class="semester-info-item">ECTS: {{ item.schedule.ects }}</div>
              <div class="semester-info-item">Exams: {{ item.schedule.examSum }}</div>
              <div v-if="item.schedule.ects > 36" class="ects-warning">
                ⚠️ Your workload for this semester is high: Your ECTS exceed the maximum recommended 36 ECTS.
              </div>
            </div>
            <div class="semester-control" @click.stop="openSemesterSettings(item.semester)">
              <div class="semester-control-item tooltip-container">
                <span :class="{ 'icon-settings-modified': !item.preference.default }"
                      class="material-symbols-outlined icon-settings">settings</span>
                      <div class="tooltip-text left">Semester settings</div>
              </div>
              <span class="tool-label">Semester settings</span>
            </div>
          </div>
          <div v-else class="semester-overview semester-overview--0">
            <div class="semester-info">
              <div class="semester-info-item">Passed modules</div>
              <div class="semester-info-item">ECTS: {{ item.schedule.ects }}</div>
            </div>
          </div>
        </div>

        <div v-if="item.schedule.modules.length !== 0" class="modules">
          <div v-for="(module, mIdx) in moduleObjects(item.schedule.modules)" :id="[module.id]"
               :key="module.id"
               :number="globalModuleNumber(item.semester, mIdx)"
               :class="[{'passed': isPassed(module.id)},
               {'required': isRequired(module.id)},
               {'structure-required': isStructureRequired(module.id)},
               {'structure-optional': isStructureOptional(module.id)},
               {'desired': isDesired(module.id)},
               {'desired-sem': isDesiredInSem(module.id)},
               {'technical': isTechnical(module)},
               structureColorClass(module.id),
               interdisciplinaryColorClass(module.id)]"
               class="module"
               @mouseenter="handleModuleMouseEnter(module.id)"
               @mouseleave="handleModuleMouseLeave(module.id)">
            <div class="module-content">
              <div class="left-content">
                <div class="module-detail">{{ (module.mock || module.suppressId) ? '' : module.number  }}</div>
                <div v-tooltip-if-cropped class="module-name">
                  {{ module.name }}{{ isTechnical(module) ? ' bestanden' : '' }}
                </div>   
                <div class="module-detail" v-if="module.ects > 0 || !module.suppressInfoModal">
                  <span v-if="module.ects > 0" >{{ module.ects }} ECTS </span>
                 <span  class="material-symbols-outlined info-icon" v-if="!module.suppressInfoModal" @click.stop="openModuleInfoModal(module,item.semester)">info</span>
                </div>
                <div class="module-detail" v-else/>
              </div>
              <div v-if="item.semester !== 0 && !isTechnical(module)" class="right-content">
                <div class="right-control">
                  <div class="icon-container tooltip-container">
                    <span v-if="isRequired(module.id)"
                          class="icon-undesire material-symbols-outlined">hide_source</span>
                    <span v-else class="icon-undesire material-symbols-outlined"
                          @click="undesire(module.id)">cancel</span>
                    <div v-if="isRequired(module.id)"
                         :class="[tooltipPositions[module.id]]"
                         class="tooltip-text">Module not removable (required)
                    </div>
                    <div v-else
                         :class="[tooltipPositions[module.id]]"
                         class="tooltip-text">Add to undesired modules
                    </div>
                  </div>
                </div>
                <div class="icon-container tooltip-container" v-if="structureColorClass(module.id)">
                <span
                    :class="[
                      { 'icon-track': !!structureColorClass(module.id) },
                      'material-symbols-outlined',
                      structureColorClass(module.id),
                      { 'desired': isDesired(module.id) },
                      { 'desired-sem': isDesiredInSem(module.id) }
                    ]"
                  @mouseenter="highlightRelatedModules(module.id)"
                  @mouseleave="resetHighlight()"
                >
                  timeline
                </span>
                <div v-if="hoveredStructureName" class="tooltip-text">
                  Highlighting modules from selected {{ hoveredStructureName }}
                </div>
                </div>
                <div class="icon-container tooltip-container" v-if="isInterdisciplinary(module.id)">
                <span
                    v-if="isInterdisciplinaryMock(module)"
                    :class="[
                      'material-symbols-outlined',
                      'icon-interdisciplinary',
                      'icon-interdisciplinary-clickable',
                      interdisciplinaryColorClass(module.id),
                      { 'desired': isDesired(module.id) },
                      { 'desired-sem': isDesiredInSem(module.id) }
                    ]"
                  @mouseenter="highlightRelatedInterdisciplinaryModules(module.id)"
                  @mouseleave="resetInterdisciplinaryHighlight()"
                  @click.stop="openModuleInfoModalWithSpecify(module, item.semester)"
                >
                  extension
                </span>
                <span
                    v-else
                    :class="[
                      'icon-interdisciplinary-concrete',
                      interdisciplinaryColorClass(module.id)
                    ]"
                  @mouseenter="highlightRelatedInterdisciplinaryModules(module.id)"
                  @mouseleave="resetInterdisciplinaryHighlight()"
                  @click.stop
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7 1.49 0 2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5s-1.12-2.5-2.5-2.5z"/>
                  </svg>
                </span>
                <div v-if="hoveredInterdisciplinaryName" class="tooltip-text">
                  {{ isInterdisciplinaryMock(module) ? 'Highlighting interdisciplinary modules (click to specify module)' : 'Highlighting interdisciplinary modules' }}
                </div>
                </div>
                <div class="right-control control-container">
                  <div :class="{'menu-active': showOptionsLec === module.id}"
                       class="icon-container tooltip-container options-control">
                    <span
                        class="icon-lock material-symbols-outlined"
                        @click="toggleOptions(module.id)">{{
                        isDesiredInSem(module.id, item.semester)
                            ? 'push_pin' : 'more_vert'
                      }}</span>
                    <div :class="tooltipPositions[module.id]" class="tooltip-text">
                      {{
                        isDesired(module.id) || isDesiredInSem(module.id) ?
                            'Show desire options' : 'Add to desired modules'
                      }}
                    </div>
                    <div v-if="showOptionsLec === module.id" class="options"
                         @click.stop="toggleOptions(module.id)">
                      <!-- unlock from plan -->
                      <div
                          v-if="!isRequired(module.id) && (isDesired(module.id))"
                          class="option-item"
                          @click="unlock(module.id)">No longer always include in plan
                      </div>
                      <div
                          v-if="!isRequired(module.id) && (isDesiredInSem(module.id, item.semester))"
                          class="option-item"
                          @click="unlock(module.id)">Unpin
                      </div>
                      <!-- lock to any semester -->
                      <div v-if="isDesiredInSem(module.id, item.semester)"
                           class="option-item"
                           @click="lock(module.id)"> {{
                          isRequired(module.id) ? 'Unpin' : 'Unpin but still include in plan'
                        }}
                      </div>
                      <div
                          v-else-if="!isRequired(module.id) && !isDesired(module.id)"
                          class="option-item"
                          @click="lock(module.id)">Always include in plan
                      </div>
                      <!-- lock to this semester -->
                      <div v-if="!isDesiredInSem(module.id, item.semester)"
                          class="option-item"
                           @click="lockToSem(module.id, item.semester)">
                        Pin to this semester
                      </div>
                      <!-- move to semester -->
                      <div v-for="semester in semesterOptions(module, item.semester)" :key="semester"
                           class="option-item"
                           @click="moveToSem(module.id, semester)">Move and pin to semester
                        {{ semester }}
                      </div>
                      <!-- <div class="option-item" 
                          @click="showModuleInTimetable(module)">
                        Show in timetable
                      </div> -->
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="item.semester !== 0 && (item.semester !== currentSemester || isStartSemesterUpcoming)" class="module module-add"
               @click="openModal({modal: 'DesiredSemesterModulesModal', semester: item.semester})">
            <!-- other options: find_replace, action_key, explore, feature_search, travel_explore -->
            <span class="material-symbols-outlined">saved_search</span>
            <span class="module-add-text">Alternative modules</span>
          </div>
          <div v-if="item.semester === currentSemester && !isStartSemesterUpcoming" class="module timetable-preview"
              @click="showSemesterTimetable(item.semester)">
            
            <span class="material-symbols-outlined">calendar_month</span>
            <span class="module-add-text">Plan Timetable</span>
            <div class="timetable-preview-grid">
              <div v-for="day in ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']" :key="day" class="timetable-preview-day">
                <div class="timetable-preview-hours">
                  <div v-for="hour in 8" :key="hour" class="timetable-preview-hour">
                    <div class="timetable-preview-slot"
                        :style="{
                          backgroundColor: getPreviewSlotColor(moduleObjects(item.schedule.modules), day, hour),
                          opacity: getPreviewSlotOpacity(moduleObjects(item.schedule.modules), day, hour)
                        }">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          <span class="material-symbols-outlined new-icon">fiber_new</span>
          </div> 
        </div>
      </div>
    </div>

  <SemesterModulesModal
    :is-open="showTimetable"
    @close="closeTimetable"
  />
  <ModuleInfoModal
    :is-open="isModuleInfoModalOpen"
      :module="selectedModule"
      :open-specify-dropdown="openSpecifyOnInfoModal"
      :pinableSems="semesterOptions(selectedModule, semOfSelectedModule)"
      :assignedSemester="semOfSelectedModule"
      :semesterDescription="openedModuleSemesterDescription"
      :required = "openedModuleRequired"
      :pinned = "openedModulePinned"
      :inUpcomingSem = "openedModuleInUpcomingSem"
      :currentSemester="currentSemester"
      :is-passed="selectedModule ? isPassed(selectedModule.id) : false"
      @close="closeModuleInfoModal"
      @change-module="handleModuleChange"
      @add-desired="lock(selectedModule.id)"
      @add-undesired="undesire(selectedModule.id)"
      @add-desired-in-sem="(item) => this.pinInfoModule(selectedModule.id, item.semester)"
      @unpin="this.unlock(selectedModule.id)"
      @specify-module="handleSpecifyModule"
    />

    <SemesterSettingsModal
      :is-open="isSemesterSettingsOpen"
      :semester="settingsSemester"
      @close="isSemesterSettingsOpen = false"
    />
  </div>
</template>


<script>
import {mapActions, mapGetters, mapState} from "vuex"
import {getSemesterDifference, isEmpty} from "@/utils";
import ModuleInfoModal from '@/components/modals/ModuleInfoModal.vue';
import SemesterModulesModal from '@/components/modals/SemesterModulesModal.vue';
import SemesterSettingsModal from '@/components/modals/SemesterSettingsModal.vue';

export default {
  components: {
    ModuleInfoModal,
    SemesterModulesModal,
    SemesterSettingsModal
  },
  directives: {
    tooltipIfCropped: {
      mounted(el) {
        if (el.scrollHeight > el.clientHeight) {
          el.setAttribute("title", el.textContent.trim());
        }
        
      }
    }
  },
  data() {
    return {
      unfoldStates: {},
      tooltipPositions: {},
      showOptionsLec: "",
      TumIcon: require("@/assets/tum.svg"),
      highlightedModules: [],
      hoveredStructureName: '',
      highlightedInterdisciplinaryModules: [],
      hoveredInterdisciplinaryName: '',
      hoveredModule: null,
      isModuleInfoModalOpen: false,
      openSpecifyOnInfoModal: false,
      bubbleClosed: false,
      selectedModule: null,
      semOfSelectedModule: null,
      currentModuleIndex: 0,
      showTimetable: false,
      isSemesterSettingsOpen: false,
      settingsSemester: null,
      currentTimetableSemester: null,
      timetableColors: [
      '#5E94D4'
      ],
      highlightedTimetableModule: null,
      isLoadingTimetable: false,
      isGeneratingPDF: false,
      isAddingToCalendar: false,
      tooltip: null,
      isGeneratingICS: false,
      showEditModal: false,
      customSessions: [], // Array to store custom sessions
      customSession: {
        name: '',
        day: '',
        startTime: '',
        endTime: '',
        location: '',
        editingSession: null,
      },
      
    };
  },
  computed: {
    ...mapGetters(["requiredModules", "reqPrefsSet", "addedPassedModules", "prefChangeDisabled", "totalECTS", "saveStart"]),
    ...mapState(["offeredSemesters", "preferences", "program", "schedule", "isLoadingSchedule",
      "isLoadingProgram", "hasSolution", "errorOccurred", "programLoaded", "violatedConstraints", "config","currentModal"]),
    openedModulePinned() {
      if (!this.selectedModule) return false;
      return this.isDesired(this.selectedModule.id) || this.isDesiredInSem(this.selectedModule.id);
    },
    openedModuleRequired() {
      if (!this.selectedModule) return false;
      return this.isRequired(this.selectedModule.id);
    },
    openedModuleInUpcomingSem() {
      if (!this.semOfSelectedModule) return false;
      const sem = this.semOfSelectedModule;
      const prevcheck = this?.schedule[sem-1]?.ects;
      return prevcheck === 0 || sem === 1;
    },
    openedModuleSemesterDescription() {
      if (!this.semOfSelectedModule) return '';
      return this?.plan[this.semOfSelectedModule-1]?.schedule?.semesterDescription || '';
    },
    allModules() {
      return this.plan.flatMap(item => this.moduleObjects(item.schedule.modules));
    },
    semesterPreferences() {
      return this.preferences.semesterPreferences;
    },
    currentSemester() {
      if (!this.preferences?.currentSemester || !this.preferences?.startSemester) return 1;
      return getSemesterDifference(this.preferences.currentSemester, this.preferences.startSemester) + 1;
    },
    isStartSemesterUpcoming() {
      if (!this.config || !this.config.currentSemester) return null;
      let sem = this.config.currentSemester;
      let type = sem[0];
      let year = parseInt(sem.slice(1));
      let upcoming = '';
      if (type === 's') {
        upcoming = 'w' + year;
      } else {
        upcoming = 's' + (year + 1).toString().padStart(2, '0');
      }
      return this.preferences.startSemester === upcoming;
    },
    filteredSchedule()                     {
      // Step 1: Determine the maximum semester with non-empty modules
      const maxSemesterWithModules = Object.entries(this.schedule).reduce((maxSemester, [semester, semesterSchedule]) => {
        const currentSemester = parseInt(semester);
        if (semesterSchedule.modules.length !== 0 && currentSemester > maxSemester) {
          return currentSemester;
        }
        return maxSemester;
      }, 0);

      // Step 2: Filter the schedule object
      return Object.fromEntries(
          Object.entries(this.schedule).filter(([semester, semesterSchedule]) => {
            return semesterSchedule.modules.length !== 0
                || (semester >= this.currentSemester && semester <= maxSemesterWithModules);
          })
      );
    },
    plan() {
      return Object.entries(this.filteredSchedule).map(([semester, semesterSchedule]) => {
        return {
          semester: parseInt(semester),
          schedule: semesterSchedule,
          preference: this.semesterPreferences[semester],
        };
      });
    },
    selectedStructures() {
      let selectedStructures = [];
      this.program.structures.forEach(structure => {
      const selectedElementId = this.preferences.structures[structure.id];
      if (selectedElementId) {
        const selectedElement = structure.elements.find(element => element.id === selectedElementId);
        if (selectedElement) {
          selectedStructures.push(selectedElement);
        }
      }
    });

      return selectedStructures;
    },
    requiredStructureModules() {
      return this.getStructureModules(true).map(item => item.moduleId);
    },
    optionalStructureModules() {
      return this.getStructureModules(false).map(item => item.moduleId);
    },
    hasTimetableData() {
      if (!this.currentTimetableSemester) return false;
      const semesterPlan = this.plan.find(s => s.semester === this.currentTimetableSemester);
      return semesterPlan && semesterPlan.schedule.modules.some(moduleId => {
        const module = this.program.modules.find(m => m.id === moduleId);
        return module && module.sessions && module.sessions.length > 0;
      });
    },
    modulesWithoutSessions() {
      if (!this.currentTimetableSemester) return [];
      
      const semesterPlan = this.plan.find(s => s.semester === this.currentTimetableSemester);
      if (!semesterPlan) return [];
      
      const modulesWithoutSessions = [];
      const moduleIds = semesterPlan.schedule.modules;
      
      moduleIds.forEach(moduleId => {
        const module = this.program.modules.find(m => m.id === moduleId);
        if (module && (!module.sessions || module.sessions.length === 0)) {
          modulesWithoutSessions.push(module.name);
        }
      });
      
      return modulesWithoutSessions;
    },
    ectsWarning() {
      const degreeConfig = this.config.degrees.find(degree => degree.id === this.preferences.degree);
      if (!degreeConfig) return null;

      const majorConfig = degreeConfig.departments.flatMap(dept => dept.majors).find(major => major.name === this.preferences.major);
      if (!majorConfig) return null;

      const { minAverageECTS,  } = majorConfig;
       
      var minAvg = minAverageECTS
      const advisor = "https://www.tum.de/en/studies/support-and-advice/support-during-studies/studentadvising";
      const semesterDifference = getSemesterDifference(this.preferences.currentSemester, this.preferences.startSemester);
      if (semesterDifference <= 0) return null;

      const totalECTS = this.preferences.passedModules.reduce((sum, moduleId) => {
        const module = this.program.modules.find(mod => mod.id === moduleId);
        return module ? sum + (module.ects || 0) : sum;
      }, 0);

      const minTotal = minAvg * semesterDifference;

      if (totalECTS < minTotal) {
        return {
          message: `You passed ${totalECTS} ECTS in total, which is below the recommended minimum of ${minTotal} ECTS for the first ${semesterDifference} semester(s). Please consider <a href="${advisor}" class="advisor-contact" target="_blank" style="color: inherit; text-decoration: underline;">contacting a student advisor.</a>`
        };
      }
      return null;
    },
    uniqueViolatedConstraints() {
      const SESSION_OVERLAP_MSG = 'You cannot take two lectures taking place at the same time. (see "ADVANCED" settings" to allow overlaping classes).';
      let hasSessionOverlap = false;
      const result = [];
      for (const c of this.violatedConstraints) {
        if (c.startsWith('You cannot take two lectures taking place at the same time')) {
          if (!hasSessionOverlap) {
            hasSessionOverlap = true;
            result.push(SESSION_OVERLAP_MSG);
          }
        } else if (!result.includes(c)) {
          result.push(c);
        }
      }
      return result;
    },
    canUndo() {
      return this.$store.state.history.length > 0; // Check if undo is possible
    },

    bubbleVisible() {
      if(this.bubbleClosed) return false;
      return (this.preferences.currentSemester == this.preferences.saveDetails.savedSemester) && this.hasSolution
    },
    IsSaveModalOpen() {
      if(!this.currentModal) return false;
      return this.currentModal.startsWith('Save');
    }
    

    
    
  },
  watch: {
    isLoadingSchedule(newValue, oldValue) {
      if (!newValue && oldValue) {
        this.afterReload();
      }
    }
  },
  async created() {
     

  },
  methods: {
    isEmpty,
    ...mapActions(["openModal","downloadPDF"]),
    restoreTimetableStyles(element, originalStyles) {
      Object.keys(originalStyles).forEach(key => {
        if (originalStyles[key] !== undefined) {
          element.style[key] = originalStyles[key];
        }
      });

      const sessions = element.querySelectorAll('.module-session');
      sessions.forEach(session => {
        session.style.transition = '';
        session.style.transform = '';
        session.style.boxShadow = '';
      });
    },

    getCleanModuleName(moduleId) {
      const name = this.getModuleName(moduleId);
      return name.replace(/[^a-zA-Z0-9]/g, '_');
    },
    reload(undo=false) {
      if (!undo){
        this.$store.commit("SAVE_STATE");
      }
      this.$store.dispatch("requestSchedule");
    },
    afterReload() {
      this.initializeUnfoldStates();
      this.initializeTooltipPositions();
    },
    moduleObjects(moduleIdList) {
      return this.program.modules
        .filter(module => moduleIdList.includes(module.id))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    },
    openModuleInfoModal(module, sem, openSpecify = false) {
      this.selectedModule = module;
      this.currentModuleIndex = this.allModules.findIndex(m => m.id === module.id);
      this.openSpecifyOnInfoModal = openSpecify;
      this.isModuleInfoModalOpen = true;
      this.semOfSelectedModule = sem;
    },
    openModuleInfoModalWithSpecify(module, sem) {
      this.openModuleInfoModal(module, sem, true);
    },
    // Helper method to open module info modal by module ID
    openModuleInfoModalById(moduleId) {
      const module = this.program.modules.find(m => m.id === moduleId);
      if (module) {
        // Find which semester this module is in
        const semesterItem = this.plan.find(item =>
          item.schedule.modules.includes(moduleId)
        );
        const semester = semesterItem ? semesterItem.semester : this.currentTimetableSemester;
        this.openModuleInfoModal(module, semester);
      }
    },
    getModuleByNumber(number) {
    if (!Array.isArray(this.modules) || this.modules.length === 0) return null;
  
    const index = (number - 1) % this.modules.length;
    return this.modules[index];
    },
    globalModuleNumber(semester, moduleIdx) {
    let count = 0;
    for (const item of this.plan) {
      if (item.semester < semester) {
        count += item.schedule.modules.length;
      }
    }
    return count + moduleIdx + 1;
    },
    
    closeModuleInfoModal() {
      this.isModuleInfoModalOpen = false;
      this.openSpecifyOnInfoModal = false;
      this.selectedModule = null;
    },

    handleSpecifyModule({ mockModuleId, realModuleId, semester }) {
      if (mockModuleId) {
        this.$store.commit("REMOVE_DESIRED_IN_SEM_MODULE", { desiredInSemModule: mockModuleId, bulk: true });
        this.$store.commit("REMOVE_DESIRED_MODULE", { desiredModule: mockModuleId, bulk: true });
      }
      if (realModuleId) {
        this.$store.commit("REMOVE_UNDESIRED_MODULE", { undesiredModule: realModuleId, bulk: true });
      }

      let targetSem = null;
      if (semester && semester !== 'Current' && semester !== 0) {
        targetSem = semester;
      } else if (semester === 'Current' || this.semOfSelectedModule) {
        targetSem = this.semOfSelectedModule;
      }

      if (targetSem) {
        this.$store.commit("ADD_DESIRED_IN_SEM_MODULE", { module: realModuleId, semester: targetSem, bulk: true });
        this.semOfSelectedModule = targetSem;
      } else {
        this.$store.commit("REMOVE_DESIRED_IN_SEM_MODULE", { desiredInSemModule: realModuleId, bulk: true });
        if (!this.requiredModules.includes(realModuleId)) {
          this.$store.commit("ADD_DESIRED_MODULE", { desiredModule: realModuleId, bulk: true });
        }
      }

      this.closeModuleInfoModal();
      this.reload();
    },

    pinInfoModule(id,sem){
      if(sem==='Current'){
        this.lockToSem(id,this.semOfSelectedModule);
      }
      else{
        this.moveToSem(id, sem);
        this.semOfSelectedModule = sem;
      }
    },
    semesterOptions(moduleObject, semester) {
      if(!moduleObject || !moduleObject.id) return [];
      const movedSem = this.preferences.desiredInSemModules[moduleObject.id];
      const offeredSems = this.offeredSemesters[moduleObject.id];
      const currentSem = this.currentSemester;
      if (offeredSems === undefined) {
        const maxSemesterWithModules = Object.entries(this.schedule).reduce((maxSemester, [semester, semesterSchedule]) => {
        const currentSemester = parseInt(semester);
        if (semesterSchedule.modules.length !== 0 && currentSemester > maxSemester) {
          return currentSemester;
        }
        return maxSemester;
      }, 0);
      return Array.from({ length: maxSemesterWithModules }, (_, i) => i + 1);
      }
      return offeredSems.filter(item => item !== semester && item !== movedSem && item !== 0 && item >= currentSem);

    },
    isPassed(moduleId) {
      return this.preferences.passedModules.includes(moduleId);
    },
    addPassed(modules){
      modules.modules.forEach(modid =>
        this.$store.commit('ADD_PASSED_MODULE', {
          passedModule: modid,
          bulk: true
        })
      )
      
      
    },
    isRequired(moduleId) {
      return this.requiredModules.some(module => module === moduleId);
    },
    isStructureRequired(moduleId) {
      return this.requiredStructureModules.some(module => module === moduleId);
    },
    findModules(blockId) {
      const block = this.program.blocks.find(b => b.id === blockId);
      if (!block) return [];

      let modules = block.modules ? [...block.modules] : [];

      if (block.blocks && block.blocks.length > 0) {
        modules = modules.concat(block.blocks.flatMap(innerBlockId => this.findModules(innerBlockId)));
      }

      return modules;
    },

    getStructureModules(required) {
      if (!this.selectedStructures.length) return [];

      return this.selectedStructures.flatMap(structure =>
        structure.blocks.flatMap(block => {
          if (block.allRequired === required) {
            let modules = block.modules ? [...block.modules] : [];
            if (block.blocks && block.blocks.length > 0) {
              modules = modules.concat(block.blocks.flatMap(blockId => this.findModules(blockId)));
            }
            return modules.map(moduleId => ({
              moduleId,
              structureId: structure.id, 
              structureName: structure.name,
              structureColor: structure.highlight,   
            }));
          }
          return [];
        })
      );
    },
    getStructures() {
      const moduleStructuresMap = new Map();
      // Get the selected elements from selectedStructures
      const selectedElements = this.selectedStructures;
      selectedElements.forEach(selectedElement => {
        // Find the structure that contains this element
        const structure = this.program.structures.find(structure =>
          structure.elements.some(element => element.id === selectedElement.id)
        );
        if (structure) {
          selectedElement.blocks.forEach(block => {
            let modules = block.modules ? [...block.modules] : [];
            if (block.blocks && block.blocks.length > 0) {
              modules = modules.concat(block.blocks.flatMap(blockId => this.findModules(blockId)));
            }
            modules.forEach(moduleId => {
              if (!moduleStructuresMap.has(moduleId)) {
                moduleStructuresMap.set(moduleId, {
                  elementId: selectedElement.id, 
                  structureId: structure.id,
                  structureName: structure.name,
                  structureColor: structure.highlight, 
                });
              }
            });
          });
        }
      });
      return Array.from(moduleStructuresMap.entries()).map(([moduleId, structure]) => ({
        moduleId,
        structure,
      }));
     },
    handleModuleMouseEnter(moduleId) {
      this.hoveredModule = moduleId;
      this.highlightRelatedModules(moduleId);
      this.highlightRelatedInterdisciplinaryModules(moduleId);
    },
    handleModuleMouseLeave() {
      this.resetHighlight();
      this.resetInterdisciplinaryHighlight();
      this.hoveredModule = null;
    },
    handleContextMenu(event) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    },
    
    highlightRelatedModules(moduleId) {
      const moduleStructure = this.getStructures().find(item => item.moduleId === moduleId);
      if (moduleStructure) {
        const { structureName, structureId } = moduleStructure.structure;
        this.hoveredStructureName = structureName;
        this.highlightedModules = this.getStructures()
          .filter(item => item.structure.structureId === structureId)
          .map(item => item.moduleId);
      }
    },
    resetHighlight() {
      this.highlightedModules = [];
      this.hoveredStructureName = '';
    },
    structureColorClass(moduleId) {
      if (this.highlightedModules.includes(moduleId)) {
        return "structure-color-orange";
      }
      const modulesWithStructure = this.getStructures();
      const moduleStructure = modulesWithStructure.find(item => item.moduleId === moduleId);

      if (moduleStructure) {
        let color = moduleStructure.structure.structureColor;
        if (moduleStructure.structure.structureId === "track") {
          color = `${color}_light`;
        }
        return `structure-color-${color}`;
      }
      return '';
    },
    getInterdisciplinaryBlockIds() {
      if (!this.program || !Array.isArray(this.program.blocks)) return new Set();
      const interdisciplinaryIds = new Set();
      this.program.blocks.forEach(block => {
        if (
          block.interdisciplinary 
        ) {
          interdisciplinaryIds.add(block.id);
        }
      });
      let addedNew = true;
      while (addedNew) {
        addedNew = false;
        this.program.blocks.forEach(block => {
          if (interdisciplinaryIds.has(block.id) && block.blocks) {
            block.blocks.forEach(subId => {
              if (!interdisciplinaryIds.has(subId)) {
                interdisciplinaryIds.add(subId);
                addedNew = true;
              }
            });
          }
        });
      }
      return interdisciplinaryIds;
    },
    getInterdisciplinaryModules() {
      if (!this.program || !Array.isArray(this.program.blocks)) return [];
      const interdisciplinaryBlockIds = this.getInterdisciplinaryBlockIds();
      const moduleMap = new Map();

      const topInterdisciplinaryBlocks = this.program.blocks.filter(block => {
        if (!interdisciplinaryBlockIds.has(block.id)) return false;
        const hasInterdisciplinaryParent = this.program.blocks.some(parent =>
          interdisciplinaryBlockIds.has(parent.id) && parent.blocks && parent.blocks.includes(block.id)
        );
        return !hasInterdisciplinaryParent;
      });

      topInterdisciplinaryBlocks.forEach(block => {
        let modules = block.modules ? [...block.modules] : [];
        if (block.blocks && block.blocks.length > 0) {
          modules = modules.concat(block.blocks.flatMap(subId => this.findModules(subId)));
        }
        modules.forEach(moduleId => {
          if (!moduleMap.has(moduleId)) {
            moduleMap.set(moduleId, {
              moduleId,
              blockId: block.id,
              blockName: block.name || 'Interdisciplinary Block'
            });
          }
        });
      });

      return Array.from(moduleMap.values());
    },
    highlightRelatedInterdisciplinaryModules(moduleId) {
      const interdisciplinaryModules = this.getInterdisciplinaryModules();
      const moduleInterdisciplinary = interdisciplinaryModules.find(item => item.moduleId === moduleId);
      if (moduleInterdisciplinary) {
        const { blockName, blockId } = moduleInterdisciplinary;
        this.hoveredInterdisciplinaryName = blockName;
        this.highlightedInterdisciplinaryModules = interdisciplinaryModules
          .filter(item => item.blockId === blockId)
          .map(item => item.moduleId);
      }
    },
    resetInterdisciplinaryHighlight() {
      this.highlightedInterdisciplinaryModules = [];
      this.hoveredInterdisciplinaryName = '';
    },
    interdisciplinaryColorClass(moduleId) {
      if (this.highlightedInterdisciplinaryModules.includes(moduleId)) {
        return "interdisciplinary-color-purple-hover";
      }
      return '';
    },
    isInterdisciplinary(moduleId) {
      return this.getInterdisciplinaryModules().some(item => item.moduleId === moduleId);
    },
    isInterdisciplinaryMock(module) {
      if (!module) return false;
      const isMock = module.mock || module.mock2 || false;
      return isMock && this.isInterdisciplinary(module.id);
    },
    isStructureOptional(moduleId) {
      return this.optionalStructureModules.some(module => module === moduleId);
    },
    isDesired(moduleId) {
      return this.preferences.desiredModules.includes(moduleId);
    },
    isDesiredInSem(moduleId, semester = undefined) {
      const sem = this.preferences.desiredInSemModules[moduleId];
      return sem !== undefined && (semester === undefined || sem === semester);
    },
    isTechnical(module) {
      return module.submodules.length !== 0;
    },
    toggleUnfolded(semesterSchedule) {
      this.unfoldStates[semesterSchedule] = !this.unfoldStates[semesterSchedule];
    },
    initializeUnfoldStates() {
      this.unfoldStates = {};
      // Use Object.keys to iterate over the keys of the filteredSchedule object
      Object.keys(this.filteredSchedule).forEach(semester => {
        this.unfoldStates[semester] = false;
      });
    },
    initializeTooltipPositions() {
      this.tooltipPositions = {};
      Object.values(this.filteredSchedule).forEach(semesterSchedule => {
        semesterSchedule.modules.forEach(moduleId => {
          // Default value
          this.tooltipPositions[moduleId] = "right";

          const buttonEl = this.$el.querySelector(`#${moduleId} .tooltip-container`);
          if (!buttonEl) return;
          const tooltipEls = buttonEl.querySelectorAll(`#${moduleId} .tooltip-text`);
          if (!tooltipEls) return;

          const buttonRect = buttonEl.getBoundingClientRect();
          const planviewElement = document.querySelector(".planview");
          const scrollbarWidth = this.hasVerticalScrollbar(planviewElement) ? this.getScrollbarWidth(planviewElement) : 0;

          for (const tooltipEl of tooltipEls) {
            const tooltipRect = tooltipEl.getBoundingClientRect();
            // Add some button width to take care of the "left: 150%" being used on the tooltip-text
            const spaceRight = window.innerWidth - (scrollbarWidth + buttonRect.right + 1.5 * buttonRect.width + tooltipRect.width);

            if (spaceRight < 0) {
              // If one of the tooltips of a module goes left, all will to avoid jumping
              this.tooltipPositions[moduleId] = "left";
              break; // Using 'break' to exit the loop once the tooltip position is determined
            }
          }
        });
      });
    },
    openSemesterSettings(semester) {
      this.settingsSemester = semester;
      this.isSemesterSettingsOpen = true;
    },

    undesire(moduleId) {
      this.$store.commit("ADD_UNDESIRED_MODULE", {undesiredModule:moduleId, bulk:true});
      this.reload();
    },
    lock(moduleId) {
      this.$store.commit("REMOVE_DESIRED_IN_SEM_MODULE", {desiredInSemModule:moduleId, bulk:true});
      if (!this.requiredModules.includes(moduleId)) {
        this.$store.commit("ADD_DESIRED_MODULE", {desiredModule:moduleId, bulk:true});
      } 
      this.reload();
    },
    lockToSem(moduleId, semester) {
      this.$store.commit("ADD_DESIRED_IN_SEM_MODULE", {module: moduleId, semester: semester, bulk:true});
      this.$store.commit("SAVE_STATE");
    },
    unlock(moduleId) {
      this.$store.commit("REMOVE_DESIRED_IN_SEM_MODULE", {desiredInSemModule:moduleId, bulk:true});
      this.$store.commit("REMOVE_DESIRED_MODULE",{desiredModule:moduleId, bulk:true});
      this.reload();
    },
    // Fixed JavaScript methods for better time alignment and positioning

    showModuleInTimetable(module) {
      const semesterItem = this.plan.find(item =>
        item.schedule.modules.includes(module.id)
      );
      if (semesterItem) {
        this.currentTimetableSemester = semesterItem.semester;
        this.showTimetable = true;
      }
    },

    getModuleSessions(module) {
      if (!module.sessions || !module.sessions.length) return [];
      return module.sessions.map(session => ({
        ...session,
        day: this.normalizeDayName(session.day),
        startTime: session.startTime,
        endTime: session.endTime,
        location: session.location
      }));
    },
    getModulesWithoutSessionIds() {
      if (!this.currentTimetableSemester) return [];
      
      const semesterPlan = this.plan.find(s => s.semester === this.currentTimetableSemester);
      if (!semesterPlan) return [];
      
      const moduleIds = [];
      const allModuleIds = semesterPlan.schedule.modules;
      
      allModuleIds.forEach(moduleId => {
        const module = this.program.modules.find(m => m.id === moduleId);
        if (module && (!module.sessions || module.sessions.length === 0)) {
          moduleIds.push(moduleId);
        }
      });
      
      return moduleIds;
    },
    closeTimetable() {
      this.showTimetable = false;
    },

    getModuleColors(modules) {
      const colors = {};
      modules.forEach(moduleId => {
        if (this.isDesired(moduleId) || this.isDesiredInSem(moduleId)) {
          colors[moduleId] = '#5E7B1E';
        } else {
          colors[moduleId] = '#5E94D4';
        }
      });
      return colors;
    },
    getModuleColor(moduleId) {
      if (this.isDesired(moduleId) || this.isDesiredInSem(moduleId)) {
          return '#5E7B1E'; // Green color for desired modules
      }
      const semesterIndex = this.plan.findIndex(item =>
        item.schedule.modules.includes(moduleId)
      );
      return this.timetableColors[semesterIndex % this.timetableColors.length];
    },

    getModuleName(moduleId) {
      const module = this.program.modules.find(m => m.id === moduleId);
      return module ? module.name : '';
    },

    getModuleSessionsById(moduleId, day) {
      const module = this.program.modules.find(m => m.id === moduleId);
      if (!module || !module.sessions) return [];
      return module.sessions
        .map(session => ({
          ...session,
          day: this.normalizeDayName(session.day),
          startTime: session.startTime,
          endTime: session.endTime,
          location: session.location
        }))
        .filter(session =>
          session.day && day &&
          session.day.toLowerCase() === day.toLowerCase()
        );
    },

    timePeriodsOverlap(start1, end1, start2, end2) {
      const startTime1 = this.timeToMinutes(start1);
      const endTime1 = this.timeToMinutes(end1);
      const startTime2 = this.timeToMinutes(start2);
      const endTime2 = this.timeToMinutes(end2);
      
      return startTime1 < endTime2 && startTime2 < endTime1;
    },

    getUniqueModuleSessionsById(moduleId, day) {
      const sessions = this.getModuleSessionsById(moduleId, day);
      let uniqueSessions = sessions.filter((session, index, self) => 
        index === self.findIndex(s => 
          s.startTime === session.startTime && 
          s.endTime === session.endTime && 
          s.location === session.location &&
          s.day === session.day
        )
      );  
      uniqueSessions = uniqueSessions.filter((session, index, self) => {
        // Check if this session overlaps with any previous session
        for (let i = 0; i < index; i++) {
          if (this.timePeriodsOverlap(
            session.startTime || session.start_time, 
            session.endTime || session.end_time,
            self[i].startTime || self[i].start_time, 
            self[i].endTime || self[i].end_time
          )) {
            return false; 
          }
        }
        return true; 
      });
      
      return uniqueSessions;
    },

    getUniqueFilteredSessions(module, day) {
      const sessions = this.filteredSessions(module, day);
      
      let uniqueSessions = sessions.filter((session, index, self) => 
        index === self.findIndex(s => 
          s.startTime === session.startTime && 
          s.endTime === session.endTime && 
          s.location === session.location &&
          s.day === session.day
        )
      );
      uniqueSessions = uniqueSessions.filter((session, index, self) => {
        // Check if this session overlaps with any previous session
        for (let i = 0; i < index; i++) {
          if (this.timePeriodsOverlap(
            session.startTime || session.start_time, 
            session.endTime || session.end_time,
            self[i].startTime || self[i].start_time, 
            self[i].endTime || self[i].end_time
          )) {
            return false;
          }
        }
        return true; 
      });

      return uniqueSessions;
    },
    shouldShowDetails(startTime, endTime) {
      // Only show details (location/time) if session is longer than 60 minutes
      const duration = this.timeToMinutes(endTime) - this.timeToMinutes(startTime);
      return duration > 60;
    },   
    confirmDeleteCustomSession(session) {
      if (confirm(`Are you sure you want to delete "${session.name}"?`)) {
        const index = this.customSessions.findIndex(s => s.id === session.id);
        if (index !== -1) {
          this.customSessions.splice(index, 1);
        }
      }
    },
    normalizeDayName(day) {
      if (!day) return '';
      const dayMap = {
        'mon': 'Monday',
        'tue': 'Tuesday', 
        'wed': 'Wednesday',
        'thu': 'Thursday',
        'fri': 'Friday',
        'sat': 'Saturday',
        'sun': 'Sunday'
      };
      return dayMap[day.toLowerCase()] || day;
    },

    timeToMinutes(timeStr) {
      if (!timeStr) return 0;
      
      const [hours, minutes] = timeStr.split(':').map(Number);
      
      if (isNaN(hours) || isNaN(minutes)) return 0;
      
      return hours * 60 + minutes;
    },

    formatTime(timeStr) {
      if (!timeStr) return '';
      return timeStr;
    },

    calculateSlotPosition(startTime, endTime) {
      if (!startTime || !endTime) return { top: 0, height: 0 };
      
      const startMinutes = this.timeToMinutes(startTime);
      const endMinutes = this.timeToMinutes(endTime);
      
      if (endMinutes <= startMinutes) return { top: 0, height: 0 };
      
      const dayStart = 8 * 60; // 8:00 AM in minutes (480 minutes)
      const dayEnd = 20 * 60; // 8:00 PM in minutes (1200 minutes)
      const totalDayMinutes = dayEnd - dayStart; // 720 minutes (12 hours)
      const clampedStart = Math.max(dayStart, Math.min(dayEnd, startMinutes));
      const clampedEnd = Math.max(dayStart, Math.min(dayEnd, endMinutes));
      const top = ((clampedStart - dayStart) / totalDayMinutes) * 100;
      const height = ((clampedEnd - clampedStart) / totalDayMinutes) * 100;
      const minHeight = 2;
      const adjustedHeight = Math.max(minHeight, height);
      
      return {
        top: Math.max(0, Math.min(100 - adjustedHeight, top)),
        height: adjustedHeight
      };
    },

    filteredSessions(module, day) {
      const sessions = this.getModuleSessions(module);
      return sessions.filter(session =>
        session.day && day &&
        session.day.toLowerCase() === day.toLowerCase()
      );
    },
    showSemesterTimetable(semester) {
      this.currentTimetableSemester = semester;
      this.showTimetable = true;
    },
    generateHourLabels() {
      const hours = [];
      for (let hour = 8; hour <= 20; hour++) {
        const timeStr = hour.toString().padStart(2, '0') + ':00';
        hours.push({
          hour: hour,
          label: timeStr,
          time24: timeStr
        });
      }
      return hours;
    },
    getSessionDurationClass(startTime, endTime) {
      if (!startTime || !endTime) return 'session-medium';
      
      const startMinutes = this.timeToMinutes(startTime);
      const endMinutes = this.timeToMinutes(endTime);
      const durationMinutes = endMinutes - startMinutes;
    
      if (durationMinutes <= 30) {
        return 'session-tiny';     // 30 minutes or less
      } else if (durationMinutes <= 60) {
        return 'session-short';    // 31-60 minutes
      } else if (durationMinutes <= 90) {
        return 'session-medium';   // 61-90 minutes
      } else {
        return 'session-long';     // More than 90 minutes
      }
    },

    getSessionDurationClassByHeight(height) {
      if (height <= 4.17) {
        return 'session-tiny';    
      } else if (height <= 8.33) {
        return 'session-short';    
      } else if (height <= 12.5) {
        return 'session-medium';  
      } else {
        return 'session-long';    
      }
    },
    hasSessionAtTime(module, day, hour) {
      if (!module.sessions || !module.sessions.length) return false;
      
      const dayMap = {
        'Mon': 'Monday',
        'Tue': 'Tuesday', 
        'Wed': 'Wednesday',
        'Thu': 'Thursday',
        'Fri': 'Friday'
      };
      const fullDay = dayMap[day];
      
      return module.sessions.some(session => {
        const sessionDay = this.normalizeDayName(session.day);
        if (!sessionDay || sessionDay.toLowerCase() !== fullDay.toLowerCase()) {
          return false;
        }
        
        const startTime = session.startTime || session.start_time;
        const endTime = session.endTime || session.end_time;
        
        if (!startTime || !endTime) return false;
        
        const startMinutes = this.timeToMinutes(startTime);
        const endMinutes = this.timeToMinutes(endTime);
        
        // Convert hour to time range (each preview hour represents 1.5 hours of real time)
        // Preview hours 1-8 represent real hours 8:00-20:00 (8am-8pm)
        const previewStartHour = 8 + ((hour - 1) * 1.5); // Start of this preview hour block
        const previewEndHour = 8 + (hour * 1.5); // End of this preview hour block
        
        const previewStartMinutes = previewStartHour * 60;
        const previewEndMinutes = previewEndHour * 60;
        
        // Check if session overlaps with this preview time block
        return (startMinutes < previewEndMinutes && endMinutes > previewStartMinutes);
      });
    },

    // Method to get the dominant color for a preview slot
    getPreviewSlotColor(modules, day, hour) {
      const colors = [];
      modules.forEach(module => {
        if (this.hasSessionAtTime(module, day, hour)) {
          colors.push(this.getModuleColor(module.id));
        }
      });
      
      if (colors.length === 0) return 'transparent';
      if (colors.length === 1) return colors[0];
      
      // If multiple sessions overlap, create a gradient or use the first color
      return colors[0];
    },

    // Method to get opacity for preview slot
    getPreviewSlotOpacity(modules, day, hour) {
      const sessionsCount = modules.filter(module => 
        this.hasSessionAtTime(module, day, hour)
      ).length;
      
      if (sessionsCount === 0) return 0;
      return Math.min(0.3 + (sessionsCount * 0.2), 0.9);
    },
    getCroppedName(name) {
      const maxLength = 20;
      return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
    },
    showTooltip(event, fullName) {
      if (fullName.length > 20) {
        this.tooltip = {
          x: event.clientX,
          y: event.clientY,
          text: fullName
        };
      }
    },

    hideTooltip() {
      this.tooltip = null;
    },
    async downloadAsICS() {
      this.isGeneratingICS = true;
      try {
        const sessions = this.getSessionsForCalendar();
        if (sessions.length === 0) {
          alert('No sessions to export');
          return;
        }

        const icsContent = this.generateICSContent(sessions);
        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        
        const title = 'semester_timetable';
        const date = new Date().toISOString().split('T')[0];
        const filename = `${title}_${date}.ics`;
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
      } catch (error) {
        console.error('Error generating ICS:', error);
        alert('Failed to generate calendar file. Please try again.');
      } finally {
        this.isGeneratingICS = false;
      }
    },

    generateICSContent(sessions) {
      const now = new Date();
      const startOfWeek = this.getStartOfCurrentWeek();
      
      let ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Study Planner//Timetable Export//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH'
      ];

      sessions.forEach((session, index) => {
        const eventDate = this.getNextDateForDay(session.day, startOfWeek);
        const startDateTime = new Date(eventDate);
        const endDateTime = new Date(eventDate);
        
        // Set times
        const [startHour, startMinute] = (session.startTime || '').split(':');
        const [endHour, endMinute] = (session.endTime || '').split(':');
        
        startDateTime.setHours(parseInt(startHour) || 0, parseInt(startMinute) || 0, 0, 0);
        endDateTime.setHours(parseInt(endHour) || 0, parseInt(endMinute) || 0, 0, 0);
        
        // Format for ICS (YYYYMMDDTHHMMSS)
        const formatDateTime = (date) => {
          return date.toISOString().replace(/[-:]/g, '').split('.')[0];
        };
        
        ics.push(
          'BEGIN:VEVENT',
          `UID:${Date.now()}-${index}@studyplanner.com`,
          `DTSTART:${formatDateTime(startDateTime)}`,
          `DTEND:${formatDateTime(endDateTime)}`,
          `SUMMARY:${session.moduleName}`,
          `DESCRIPTION:${session.moduleName}\\nTime: ${session.startTime} - ${session.endTime}${session.location ? '\\nLocation: ' + this.getLocationString(session.location) : ''}`,
          session.location ? 
          `LOCATION:${this.getLocationString(session.location)}` : '',
          `RRULE:FREQ=WEEKLY;COUNT=15`, // Repeat for 15 weeks
          `CREATED:${formatDateTime(now)}`,
          `LAST-MODIFIED:${formatDateTime(now)}`,
          'END:VEVENT'
        );
      });
      
      ics.push('END:VCALENDAR');
      return ics.filter(line => line !== '').join('\r\n');
    },

    getStartOfCurrentWeek() {
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Get Monday
      const monday = new Date(today);
      monday.setDate(today.getDate() + mondayOffset);
      monday.setHours(0, 0, 0, 0);
      return monday;
    },

    getNextDateForDay(dayName, startDate = null) {
      const baseDate = startDate || new Date();
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const targetDay = days.indexOf(dayName);
      
      if (targetDay === -1) return baseDate;
      
      const result = new Date(baseDate);
      const currentDay = result.getDay();
      const daysUntilTarget = (targetDay - currentDay + 7) % 7;
      
      if (daysUntilTarget === 0 && startDate) {
        // If it's the same day and we have a start date, use that date
        return result;
      }
      
      result.setDate(result.getDate() + daysUntilTarget);
      return result;
    },

    openEditTimetable() {
      this.showEditModal = true;
      this.editingSession = null;
      this.customSession = {
        name: '',
        day: '',
        startTime: '',
        endTime: '',
        location: ''
      };
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingSession = null;
      this.customSession = {
        name: '',
        day: '',
        startTime: '',
        endTime: '',
        location: ''
      };
    },

    saveCustomSession() {
      // Validate times
      if (this.customSession.startTime >= this.customSession.endTime) {
        alert('End time must be after start time');
        return;
      }

      const session = {
        id: this.editingSession ? this.editingSession.id : Date.now(),
        name: this.customSession.name,
        day: this.customSession.day,
        startTime: this.customSession.startTime,
        endTime: this.customSession.endTime,
        location: this.customSession.location || null
      };

      if (this.editingSession) {
        // Update existing session
        const index = this.customSessions.findIndex(s => s.id === this.editingSession.id);
        if (index !== -1) {
          this.customSessions.splice(index, 1, session);
        }
      } else {
        // Add new session
        this.customSessions.push(session);
      }

      this.closeEditModal();
    },

    getLocationString(location) {
      if (!location) return '';
      if (typeof location === 'string') return location;
      return location.locationName || location.location || '';
    },

    editCustomSession(session) {
      this.editingSession = session;
      this.customSession = {
        name: session.name,
        day: session.day,
        startTime: session.startTime,
        endTime: session.endTime,
        location: this.getLocationString(session.location) || ''
      };
      this.showEditModal = true;
    },

    deleteCustomSession() {
      if (this.editingSession) {
        const index = this.customSessions.findIndex(s => s.id === this.editingSession.id);
        if (index !== -1) {
          this.customSessions.splice(index, 1);
        }
        this.closeEditModal();
      }
    },

    getCustomSessionsForDay(day) {
      return this.customSessions.filter(session => 
        session.day.toLowerCase() === day.toLowerCase()
      );
    },

    // Updated getSessionsForCalendar to include custom sessions
    getSessionsForCalendar() {
      const sessions = [];
      
        // Get sessions for current semester
        const currentSemester = this.plan.find(s => s.semester === this.currentTimetableSemester);
        if (currentSemester) {
          const moduleObjs = this.moduleObjects(currentSemester.schedule.modules);
          moduleObjs.forEach(module => {
            if (module.sessions) {
              module.sessions.forEach(session => {
                sessions.push({
                  day: session.day,
                  startTime: session.startTime || session.start_time,
                  endTime: session.endTime || session.end_time,
                  location: this.getLocationString(session.location),
                  moduleName: module.name,
                  moduleId: module.id
                });
              });
            }
          });
        }
        
        // Add custom sessions
        this.customSessions.forEach(session => {
          sessions.push({
            day: session.day,
            startTime: session.startTime,
            endTime: session.endTime,
            location: this.getLocationString(session.location),
            moduleName: session.name,
            moduleId: 'custom'
          });
        });
      
      return sessions;
    },

    moveToSem(moduleId, semester) {
      this.$store.commit("ADD_DESIRED_IN_SEM_MODULE", {module: moduleId, semester: semester, bulk:true});
      this.reload();
    },
    undoLastChange() {
      this.$store.commit('UNDO_CHANGE');
      this.reload(true);
    },
    toggleOptions(moduleId) {
      try {
        if (this.showOptionsLec === moduleId) {
          this.showOptionsLec = "";
          document.removeEventListener("click", this.outsideClickListener);
        } else {
          if (this.showOptionsLec !== "") {
            document.removeEventListener("click", this.outsideClickListener);
          }
          this.showOptionsLec = moduleId;
          this.$nextTick(() => {
            this.adjustMenuPosition();
            setTimeout(() => {
              document.addEventListener("click", this.outsideClickListener);
            }, 0);
          });
        }
      } catch (error) {
        console.error("Error in toggleOptions:", error);
        this.showOptionsLec = "";
      }
    },
    outsideClickListener(event) {
      try {
        const menuEl = this.$el?.querySelector(".options");
        const buttonEl = this.$el?.querySelector(".options-control.menu-active");

        if (!event?.target) {
          this.showOptionsLec = "";
          document.removeEventListener("click", this.outsideClickListener);
          return;
        }

        if (
          (menuEl && !menuEl.contains(event.target)) &&
          (buttonEl && !buttonEl.contains(event.target))
        ) {
          this.showOptionsLec = "";
          document.removeEventListener("click", this.outsideClickListener);
        }
      } catch (error) {
        console.error("Error in outsideClickListener:", error);
        this.showOptionsLec = "";
        document.removeEventListener("click", this.outsideClickListener);
      }
    },
    hasVerticalScrollbar(element) {
      return element.scrollHeight > element.clientHeight;
    },
    getScrollbarWidth(element) {
      return element.offsetWidth - element.clientWidth;
    },
    adjustMenuPosition() {
      try {
        const menuEl = this.$el?.querySelector(".options");
        const buttonEl = this.$el?.querySelector(".options-control.menu-active");

        if (!buttonEl || !menuEl) return;

        const buttonRect = buttonEl.getBoundingClientRect();
        const menuRect = menuEl.getBoundingClientRect();

        const planviewElement = document.querySelector(".planview");
        if (!planviewElement) return;

        const scrollbarWidth = this.hasVerticalScrollbar(planviewElement) 
          ? this.getScrollbarWidth(planviewElement) 
          : 0;
        const spaceRight = window.innerWidth - (scrollbarWidth + buttonRect.right + menuRect.width);
        const spaceBelow = window.innerHeight - (buttonRect.bottom + menuRect.height);

        if (spaceRight < 0) {
          menuEl.style.left = "auto";
          menuEl.style.right = "120%";
        } else {
          menuEl.style.left = "120%";
          menuEl.style.right = "auto";
        }

        if (spaceBelow < 0) {
          menuEl.style.bottom = "0";
          menuEl.style.top = "auto";
        } else {
          menuEl.style.top = "0";
          menuEl.style.bottom = "auto";
        }
    
      }catch (error) {
            console.error("Error in adjustMenuPosition:", error);
          }
    },
    resetPreferences() {
      this.$store.commit('REDIRECT_TO_HOME');
      },
    handleModuleChange(direction) {
      if (direction === 'prev') {
        
        this.currentModuleIndex = (this.currentModuleIndex - 1 + this.allModules.length) % this.allModules.length;
        //skip mock modules
        while(this.allModules[this.currentModuleIndex].mock) {
          this.currentModuleIndex = (this.currentModuleIndex - 1 + this.allModules.length) % this.allModules.length;
        }
      } else if (direction === 'next') {
        this.currentModuleIndex = (this.currentModuleIndex + 1) % this.allModules.length;
        //skip mock modules
        while(this.allModules[this.currentModuleIndex].mock) {
          this.currentModuleIndex = (this.currentModuleIndex + 1) % this.allModules.length;
        }
      
      }

      let module = this.allModules[this.currentModuleIndex];
      // Find the assigned semester of the module in the plan
      for(let i = 0; i < this.plan.length; i++) {
        let item = this.plan[i]
        if(item.schedule.modules.includes(module.id)) {
          let sem= item.semester;
          this.openModuleInfoModal(module, sem);
          break;
        }
      }
    },
    closeBubble() {
      this.bubbleClosed = true;
    }


  },
  async mounted() {
    window.addEventListener("error", this.handleGlobalError);
    window.addEventListener("resize", this.adjustMenuPosition);
    window.addEventListener("resize", this.initializeTooltipPositions);
    setTimeout(() => {
    this.bubbleClosed = true;
  }, 11000);
    
    
  
  },
  beforeUnmount() {
    document.removeEventListener("click", this.outsideClickListener);
    window.removeEventListener("error", this.handleGlobalError);
    window.removeEventListener("resize", this.adjustMenuPosition);
    window.removeEventListener("resize", this.initializeTooltipPositions);
    
  },
  
};
</script>

<style scoped>
.planview {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: stretch;
  color: #333333;
  position: relative;
}

.plan {
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
}

.overview {
  display: flex;
  flex-direction: row;
  padding: 1px 10px 5px 10px;
  border-bottom: 1px solid #ccc;
  font-size: 80%;
  line-height: 150%;
  margin-top: 6px;
  gap: 20px;
}

.overview-item:first-child {
  font-weight: bold;

}

.semester {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #ccc;
  font-size: 80%;
  line-height: 150%;
}

.modules {
  display: flex;
  min-height: 120px;
  padding: 0 5px 5px 5px;
  flex-wrap: wrap;
}

.semester--0 {
  background-color: #f4f4f4;
}

.semester--0 .module {
  background-color: #fcfcfc;
}

.module {
  min-width: 100px;
  max-width: 200px;
  height: 85px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin: 5px;
  transition: border-color 0.3s;
  background-color: white;
}

.icon-container {
  display: flex;
  align-items: center;
}


.material-symbols-outlined {
  font-size: 18px;
  color: #333A41;
  opacity: 0.4;
  transition: opacity 0.3s;
}

.material-symbols-outlined.noplan-icon {
  opacity: 0.5;
}

.module .material-symbols-outlined {
  cursor: pointer;
}

.module .material-symbols-outlined:hover {
  opacity: 1;
}

.menu-active .material-symbols-outlined {
  opacity: 1;
}

.module.required .icon-undesire, .module.structure-required .icon-undesire, .module .icon-track {
  cursor: default;
}

.module.required .icon-undesire:hover, .module.structure-required .icon-undesire:hover, .module .icon-track:hover {
  opacity: 0.3;
  cursor: default;
}

.module.desired .icon-lock {
  color: #C7D97D;
}


.module.desired-sem .icon-lock {
  color: #5E7B1E;
}

.module.desired .icon-lock, .module.desired-sem .icon-lock {
  opacity: 1;
}

.module.desired .icon-lock:hover, .module.desired-sem .icon-lock:hover {
  opacity: 0.7;
}

.module.technical {
  opacity: 0.6;
}

.module.structure-color-orange {
  background-color: rgba(249, 191, 78, 0.6) !important;
}

.module.structure-color-orange_light {
  background-color: rgba(249, 191, 78, 0.6) !important;
}

.module.desired {
  border-color: #C7D97D;
  border-width: 3px;
  padding: 9px;
}

.module.desired-sem {
  border-color: #5E7B1E;
  border-width: 3px;
  padding: 9px;
}

.module.desired.structure-color-orange,
.module.desired-sem.structure-color-orange,
.module.desired.structure-color-orange_light,
.module.desired-sem.structure-color-orange_light {
  background-color: rgba(249, 191, 78, 0.6) !important;
}

.module {
  cursor: pointer;
}

.module:hover {
  cursor: pointer;
}

.icon-track {
  color:  #D95117;
}

.icon-track.structure-color-orange {
  color: grey;
}

.icon-track.structure-color-orange_light {
  color: orange; 
}

.icon-track.structure-color-green {
  color: grey;
}

.icon-track.structure-color-green_light {
  color: grey; 
}

.icon-track.desired {
  color: grey; 
}

.icon-track.desired-sem {
  color: grey; 
}

.module.interdisciplinary-color-purple-hover {
  background-color: rgba(228, 200, 255, 0.65) !important;
}

.module.desired.interdisciplinary-color-purple-hover,
.module.desired-sem.interdisciplinary-color-purple-hover {
  background-color: rgba(228, 200, 255, 0.65) !important;
}

.icon-interdisciplinary {
  color: #7C3AED;
  opacity: 0.65;
  font-variation-settings: 'FILL' 0;
}

.icon-interdisciplinary-clickable {
  cursor: pointer;
  transition: transform 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}

.icon-interdisciplinary-clickable:hover {
  color: #9333EA;
  cursor: pointer;
  transform: scale(1.15);
  opacity: 1 !important;
}

.icon-interdisciplinary-concrete,
.module .icon-interdisciplinary-concrete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #8b5fd7 !important;
  opacity: 1 !important;
  cursor: default !important;
  width: 18px;
  height: 18px;
  transform: none !important;
}

.icon-interdisciplinary-concrete svg {
  display: block;
  width: 18px;
  height: 18px;
  fill: #8b5fd7 !important;
}

.icon-interdisciplinary-concrete:hover,
.module .icon-interdisciplinary-concrete:hover {
  color: #8b5fd7 !important;
  opacity: 1 !important;
  cursor: default !important;
  transform: none !important;
}

.icon-interdisciplinary-concrete:hover svg,
.module .icon-interdisciplinary-concrete:hover svg {
  fill: #7C3AED !important;
}

.tooltip-text {
  font-size: 14px;
}

.icon-track:hover {
  cursor: default;
}

.module-add {
  min-width: unset;
  width: 85px;
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
}

.module-add:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.module-add .material-symbols-outlined {
  font-size: 40px;
  transition: opacity 0.2s;
}

.module-add:hover .material-symbols-outlined {
  opacity: 1;
}

.module-add-text {
  text-align: center;
}

.module-content {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr auto; 
  grid-template-rows: 1fr 2fr 1fr;
  gap: 12px;
  position: relative; 
  width: 100%;
}

.left-content {
  grid-column: 1 / 2;
  grid-row: 1 / -1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0; 
}

.right-content {
  grid-column: 2 / 3;
  grid-row: 1 / -1;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
}

.module-detail {
  line-height: 1.5em;
  min-height: 1.5em;
}

.module-name {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: bold;
  margin: 5px 0;
}

.semester-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.semester-overview {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 10px 5px 10px;
}

.semester-overview:not(.semester-overview--0) {
  cursor: pointer;
  transition: background-color 0.3s;
}

.semester-overview:not(.semester-overview--0):hover {
  background-color: #EBECEF;
}

.semester-info {
  display: flex;
  justify-content: left;
}

.semester-info-item {
  margin: 5px 20px 0 0;
}

.semester-info-item:first-child {
  font-weight: bold;
}
.ects-warning {
  color: #fff;
  background-color: #EA7237 ;
  border-left: 4px solid #fdf6f0;
  padding: 10px 20px;
  margin: 0px 0 0 0;
  border-radius: 2px;
  font-size: 1em;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  line-height: 0.8;
}



.semester-control {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
}

.semester-control-item {
  display: flex;
  align-items: flex-end;
}

.material-symbols-outlined {
  font-variation-settings: 
    'FILL' 0,
    'wght' 400,
    'GRAD' 0,
    'opsz' 24;
}

.icon-settings {
  cursor: pointer;
  font-size: 24px; 
  color: #333; 
}

.icon-settings-modified {
  color: orange;
}

.icon-local {
  color: #9ABCE4;
}

.info-icon {
  cursor: pointer;
  opacity: 0.6;
  margin-left: 0px;
  font-size: 16px !important;
  vertical-align: middle;
}

.info-icon:hover {
  opacity: 1;
}

.btnContainer {
  display: flex;
  gap: 10px;
}

.defaultBtn {
  margin-top: 5px;
  padding: 5px 10px;
  border: none;
  background-color: #999999;
  color: #ffffff;
  border-radius: 2px;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s;
}

.defaultBtn:hover {
  background-color: #777777;
}

.control-container {
  position: relative;
}

.options {
  position: absolute;
  z-index: 2;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  left: 100%; 
  top: 0;
  width: auto;
  height: auto;
  white-space: nowrap;
}

.option-item {
  cursor: pointer;
  border-radius: 4px;
  padding: 5px;
  transition: background-color 0.3s;
}

.option-item:not(.option-item-inactive):hover {
  background-color: #f0f0f0;
}

.option-item.option-item-inactive {
  color: #ccc;
  cursor: inherit;
}

.noplan {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.noplan-content {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 20px;
  text-align: left;
  padding: 20px;
  max-width: 80%;
}

.noplan-icon {
  font-size: 40px;
  margin-top: 4px;
}

.noplan-text-headline {
  font-weight: bold;
}

.noplan-list {
  margin: 10px 0;
  padding-inline-start: 25px;
}

.noplan-list li:not(:last-child) {
  margin-bottom: 5px;
}

.clickable-text {
  cursor: pointer;
  text-decoration: underline;
}

.warning-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: left;
  font-size: 14px;
  color: #fff;
  background-color: #D95117;
}

.warning-message {
  font-weight: bold;
  margin: 7px 10px;
}

.icon-warning {
  font-size: 2em;
  color: #fff;
  opacity: 1.0;
  margin-top: 5px;
  margin-left: 10px;
}

.advisor-contact {
 font-weight: bold;
 color: #fff;
 margin-left: 10px;
 text-decoration: underline;
}

@keyframes flip {
  0% {
    transform: rotateY(0deg);
  }
  100% {
    transform: rotateY(1turn);
  }
}

.loading-icon {
  animation: flip 2s infinite linear;
  width: 36px;
  height: 36px;
}

.undo-container {
  margin-top: 16px;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 18px;
  display: flex;
  gap: 10px;
}

.undo-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  cursor: pointer;
  border-radius: 5px; 
  transition: box-shadow 0.3s;
  color: white;
  border: 1px solid #5E94D4;
  background-color: #5E94D4;
  font-size: 16px;
}

.undo-button:hover {
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.undo-button:disabled {
  border-color: #DDE2E6;
  background-color: #DDE2E6;
  font-size: 16px;
  cursor: not-allowed; 
}





/* Updated CSS with responsive fixes and alignment corrections */

.close-icon {
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #6c757d;
  font-size: 24px;
  margin-left: 8px;
}

.close-icon:hover {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.hours-header {
  height: 48px; /* Matched with day-header */
  border-bottom: 2px solid #e9ecef;
  background-color: #f8f9fa;
  flex-shrink: 0;
}

.hours-grid {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.hour-slot {
  height: calc(100% / 12);
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 2px; /* Reduced for better alignment */
  box-sizing: border-box;
}

.hour-label {
  font-size: 11px;
  font-weight: 500;
  color: #495057;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 2px 4px;
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  z-index: 1;
  line-height: 1.2;
}

.hour-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, #dee2e6 20%, #dee2e6 80%, transparent 100%);
}

.day-header {
  text-align: center;
  padding: 12px 6px;
  font-weight: 600;
  font-size: 12px;
  color: #495057;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-bottom: 2px solid #e9ecef;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  height: 48px; /* Fixed height to match hours-header */
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.day-content {
  position: relative;
  flex: 1;
  overflow: hidden;
}

.day-grid {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

.time-grid-line {
  flex: 1;
  border-bottom: 1px solid #f1f3f4;
  position: relative;
  height: calc(100% / 12); /* Perfect alignment with hour slots */
}

.time-grid-line:nth-child(even) {
  background-color: rgba(248, 249, 250, 0.5);
}

.time-grid-line:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, #e9ecef 10%, #e9ecef 90%, transparent 100%);
}

.day-slots {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 0 2px;
}

/* Session styles */
.module-session {
  position: absolute;
  left: 4px;
  right: 4px;
  border-radius: 4px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 4px 6px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  z-index: 3;
  min-height: 30px;
  backdrop-filter: blur(4px);
  box-shadow: 
    0 2px 8px rgba(0, 0, 0, 0.1),
    0 1px 3px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
}

.module-session.custom-session {
  border-left: 4px solid #495057;
  background-color: #6c757d !important;
}

/* Duration-based session styles */
.module-session.session-tiny {
  padding: 2px 4px;
  min-height: 24px;
}

.module-session.session-tiny .session-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
}

.module-session.session-tiny .module-session-name {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
}

.module-session.session-tiny .session-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  font-size: 9px;
  white-space: nowrap;
}

.module-session.session-tiny .session-location {
  display: none;
}

.module-session.session-short {
  padding: 3px 5px;
  min-height: 28px;
  align-items: center;
}

.module-session.session-short .session-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
}

.module-session.session-short .module-session-name {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.module-session.session-short .session-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 3px;
  font-size: 8px;
  flex-shrink: 0;
  white-space: nowrap;
}

.module-session.session-medium {
  padding: 4px 6px;
  min-height: 35px;
}

.module-session.session-medium .session-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.module-session.session-medium .module-session-name {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
  line-height: 1.1;
}

.module-session.session-medium .session-details {
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 8px;
}

.module-session.session-long {
  padding: 5px 7px;
  min-height: 40px;
}

.module-session.session-long .session-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
}

.module-session.session-long .module-session-name {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 3px;
  line-height: 1.2;
}

.module-session.session-long .session-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 9px;
}

/* Default session info styles */
.session-info {
  color: white;
  font-size: 10px;
  line-height: 1.3;
  overflow: hidden;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
}

.module-session-name {
  font-weight: 700;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
  line-height: 1.2;
}

.session-details {
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 8px;
  opacity: 0.95;
}

.session-time {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-location {
  display: flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400;
}

.location-icon {
  font-size: 7px;
  flex-shrink: 0;
}

/* Hover effects */
.module-session:hover {
  transform: translateY(-1px) scale(1.02);
  box-shadow: 
    0 4px 16px rgba(0, 0, 0, 0.15),
    0 2px 6px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  z-index: 4;
  border-color: rgba(255, 255, 255, 0.5);
}
.module-session.session-tiny:hover .session-details,
.module-session.session-short:hover .session-details {
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 9px;
  z-index: 10;
}

/* Tooltip */
.timetable-tooltip {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  pointer-events: none;
  z-index: 10000;
  max-width: 250px;
  white-space: nowrap;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Edit Modal Styles */
.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.form-actions-right {
  display: flex;
  gap: 12px;
}



/* Responsive Design */
@media (max-width: 1024px) {
  .timetable-container {
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
  }
  
  .timetable-overlay {
    padding: 10px;
  }
}

@media (max-width: 768px) {
  .timetable-container {
    border-radius: 8px;
    min-height: 450px;
    width: 100%;
    height: 85vh;
    max-width: calc(100vw - 20px);
  }
  
  .timetable-overlay {
    z-index: 1040; 
  }
  
  .timetable-header {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .timetable-header h2 {
    text-align: center;
    font-size: 18px;
  }
  
  .timetable-header-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .edit-timetable-btn,
  .pdf-download-btn,
  .calendar-download-btn {
    min-width: 80px;
    padding: 6px 10px;
    font-size: 12px;
  }
  
  .btn-text {
    display: none;
  }
  
  .timetable-hours {
    width: 50px;
  }
  
  .hour-label {
    font-size: 9px;
    padding: 1px 3px;
  }
  
  .day-header {
    font-size: 10px;
    padding: 8px 4px;
    height: 36px;
  }
  
  .hours-header {
    height: 36px;
  }
  
  .module-session {
    left: 2px;
    right: 2px;
    padding: 2px 4px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .edit-modal {
    margin: 10px;
    max-width: calc(100vw - 20px);
  }
  
  .edit-modal-content {
    padding: 16px;
  }
}
@media (max-width: 480px) {
  .timetable-header-actions {
    gap: 6px;
  }
  
  .edit-timetable-btn,
  .pdf-download-btn,
  .calendar-download-btn {
    min-width: 40px;
    padding: 6px;
  }
  
  .close-icon {
    font-size: 20px;
    padding: 4px;
  }
  
  .module-session.session-tiny .module-session-name,
  .module-session.session-short .module-session-name {
    font-size: 9px;
  }
  
  .module-session.session-tiny .session-details,
  .module-session.session-short .session-details {
    font-size: 8px;
  }
}

/* Loading states */
.edit-timetable-btn:disabled,
.pdf-download-btn:disabled,
.calendar-download-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.pdf-download-btn:disabled .material-symbols-outlined,
.calendar-download-btn:disabled .material-symbols-outlined {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.timetable-preview {
  min-width: unset;
  width: 100%;
  height: 120px; 
  max-height: 105px;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 6px; 
  cursor: pointer;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #dee2e6;
  border-radius: 8px;
  box-sizing: border-box;
}
.timetable-preview:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
  border-color: #5E94D4;
}

.timetable-preview .material-symbols-outlined {
  font-size: 20px;
  transition: all 0.2s;
  opacity: 0.8;
  color: #5E94D4;
  margin-bottom: 4px;
}

.timetable-preview:hover .material-symbols-outlined {
  opacity: 1;
  transform: scale(1.1);
}

.module-add-text {
  font-size: 11px;
  font-weight: 500;
  color: #495057;
  text-align: center;
  margin-bottom: 6px;
  line-height: 1.2;
}

.timetable-preview-grid {
  display: flex;
  width: 100%;
  height: 50px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  padding: 2px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.timetable-preview-day {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
}

.timetable-preview-day:last-child {
  border-right: none;
}

.timetable-preview-hours {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

.timetable-preview-hour {
  flex: 1;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
  position: relative;
  min-height: 0;
}

.timetable-preview-hour:last-child {
  border-bottom: none;
}

.timetable-preview-slot {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  transition: all 0.3s ease;
  border-radius: 1px;
}

.timetable-preview:hover .timetable-preview-slot {
  opacity: 1 !important;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
}

.new-icon.new-icon {
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 28px;
  color: #28a745;
}

.warning-icon-large {
  font-size: 24px !important;
  color: #f0ad4e;
  font-variation-settings: 'FILL' 1;
}

.modules-without-sessions-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.module-box-no-session {
  padding: 8px 12px;
  border-radius: 6px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.delete-session-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 4px;
  padding: 2px;
  width: 20px;
  height: 20px;
}

.delete-session-btn:hover {
  background-color: rgba(220, 53, 69, 0.9);
}

.bubble-notification {
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #D95117;
      color: #ffffff;
      padding: 7px 35px;
      border-radius: 20px;
      box-shadow: 0 4px 10px rgba(0,0,0,0.2);
      font-size: 13px;
      max-width: 450px;
      z-index: 1000;
      animation: slideInBubble 0.5s ease-out;
    }

    .bubble-notification a {
      color: #ffffff;
      font-weight: bold;
      text-decoration: underline;
        cursor: pointer;
    }

    .bubble-notification .close-btn {
      position: absolute;
      top: 5px;
      right: 10px;
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      color: #333;
    }

    @keyframes slideInBubble {
      from { transform: translateY(100px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

.bubble-fade-leave-active {
  transition: opacity 0.6s, transform 0.6s;
}
.bubble-fade-leave-to {
  opacity: 0;
  transform: translateY(50px);
}
.tool-label {
  margin-left: 5px;
  font-size: 12px;
  padding-bottom: 2px;
  white-space: nowrap;
  color: #6A757E;
}
</style>
